import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Key, Eye, EyeOff, Copy, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';

/**
 * External API Keys · CEO-only tile.
 *
 * Surfaces the configured `EXTERNAL_API_KEY_*` env vars as masked
 * entries with a one-click "Reveal" + "Copy". Sister products
 * (e.g. 247concierge.ai) read these out of ScaleBoard's dashboard
 * and paste them into their own vaults — Pattern #16.
 *
 * Backend:
 *   GET  /api/admin/external-api-keys/list?reveal=0|1
 *   POST /api/admin/external-api-keys/reload
 */
export default function ExternalApiKeysTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchKeys = async (reveal = false) => {
    setLoading(true);
    try {
      const r = await api.get('/admin/external-api-keys/list', { params: reveal ? { reveal: 1 } : {} });
      setData(r.data);
      if (reveal) setRevealed(true);
    } catch (e) {
      toast.error(`Could not load keys: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeys(false); }, []);

  const reveal = async () => {
    if (revealed) { setRevealed(false); fetchKeys(false); return; }
    if (!window.confirm('Show full API keys on screen?\n\nAnyone watching your screen will see them. Click OK only if you are alone.')) return;
    fetchKeys(true);
  };

  const reload = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/admin/external-api-keys/reload');
      toast.success(`Reloaded ${r.data.consumers_loaded} consumer key(s).`);
      fetchKeys(revealed);
    } catch (e) {
      toast.error(`Reload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const copy = async (key, name) => {
    if (!key) { toast.error('Reveal first to copy.'); return; }
    try {
      await navigator.clipboard.writeText(key);
      toast.success(`API key for "${name}" copied to clipboard.`);
    } catch (e) {
      toast.error(`Copy failed: ${e?.message || 'unknown error'}`);
    }
  };

  if (loading && !data) {
    return (
      <div className="rounded-xl border p-4 text-xs" style={{ borderColor: '#E5E1DC', background: '#FAF8F5', color: '#6B7280' }}>
        Loading external API keys…
      </div>
    );
  }

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: '#E5E1DC', background: 'linear-gradient(135deg, #FAF8F5 0%, #F0EBE3 100%)' }}
      data-testid="external-api-keys-tile">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1B2A4A' }}>
            <Key size={18} style={{ color: '#F5C86B' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>External API Keys</p>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold" style={{ backgroundColor: '#1B2A4A', color: '#F5C86B' }}>
                {data?.namespace || '/api/external/v1/'}
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Read-only · X-API-Key auth · 100 req/min · Audit-logged · Pattern #16
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={reload} disabled={refreshing}
            title="Re-read env vars after rotating a key (no backend restart)"
            className="px-2 py-1.5 rounded text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-60"
            style={{ borderColor: '#9CA3AF', color: '#1B2A4A' }}
            data-testid="external-api-keys-reload-btn">
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> Reload
          </button>
          <button onClick={reveal}
            className="px-3 py-1.5 rounded text-[11px] font-medium text-white inline-flex items-center gap-1"
            style={{ backgroundColor: revealed ? '#A16207' : '#2A7B88' }}
            data-testid="external-api-keys-reveal-btn">
            {revealed ? <EyeOff size={11} /> : <Eye size={11} />}
            {revealed ? 'Hide' : 'Reveal'}
          </button>
        </div>
      </div>

      {(!data?.keys || data.keys.length === 0) ? (
        <div className="rounded-md border p-3 text-[11.5px] flex items-center gap-2"
          style={{ borderColor: '#E5E1DC', background: '#FFFBEB', color: '#7A4F08' }}>
          <AlertTriangle size={12} /> No external API keys configured. Set <code>EXTERNAL_API_KEY_&lt;NAME&gt;</code> in backend .env then click Reload.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="external-api-keys-list">
          {data.keys.map((k) => (
            <li key={k.env_var} className="rounded-md border p-3 bg-white" style={{ borderColor: '#E5E1DC' }}
              data-testid={`external-api-key-${k.consumer}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold" style={{ color: '#1B2A4A' }}>
                    {k.consumer}{' '}
                    <span className="text-[10px] font-mono font-normal" style={{ color: '#6B7280' }}>· {k.env_var}</span>
                  </div>
                  <div className="text-[10.5px] mt-0.5" style={{ color: '#6B7280' }}>
                    Audit log: <code>{k.audit_log_collection}</code>
                  </div>
                </div>
                <ShieldCheck size={14} style={{ color: '#16653A' }} />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <code
                  className="flex-1 px-2 py-1.5 rounded text-[11px] font-mono break-all"
                  style={{ background: '#F4F2EE', color: revealed ? '#1B2A4A' : '#9CA3AF' }}
                  data-testid={`external-api-key-value-${k.consumer}`}>
                  {revealed ? (k.key || '(error · re-reveal)') : k.key_masked}
                </code>
                <button
                  onClick={() => copy(k.key, k.consumer)}
                  disabled={!revealed || !k.key}
                  className="px-2 py-1.5 rounded text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
                  style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }}
                  data-testid={`external-api-key-copy-${k.consumer}`}>
                  <Copy size={11} /> Copy
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 pt-3 border-t text-[10.5px] leading-relaxed" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
        <strong style={{ color: '#1B2A4A' }}>To rotate a key:</strong>{' '}
        Update <code>EXTERNAL_API_KEY_&lt;NAME&gt;</code> in backend .env → click <em>Reload</em> above.
        No backend restart needed. The previous key stops working immediately.
      </div>
    </div>
  );
}
